import React from "react";
import InscriptionInput from "./InscriptionInput";
import Grades from "./Grades";
import { DegreesQuery } from "../apollo/queries/DegreesQuery";
import { LatestInscriptionQuery } from "../apollo/queries/InscriptionQuery";
import { SendNotificationQuery } from "../apollo/queries/SendNotificationQuery";
import CiberAlerts from "../util/Messages";
import { calculateAge, getDeviceId } from "../util/Utils";
import { Mutation, Query } from "@apollo/react-components";
import { InscriptionMutation } from "../apollo/mutations/InscriptionMutation";
import DocumentTypes, { documentTypes } from "./DocumentTypes";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import es from 'date-fns/locale/es';
import "react-datepicker/dist/react-datepicker.css";
import Spinner from "react-bootstrap/Spinner";
import ModalConfirmEmail from "./ModalConfirmEmail";
import { logEvent, InscriptionEvents } from "../util/analytics";
import { LightenDarkenColor } from "lighten-darken-color";
import { ApolloConsumer } from "@apollo/react-components";
import { getInstitutionIdFromToken } from "../util/JwtUtils";

// Registrar la localización en español
registerLocale('es', es);

export default class InscriptionForm extends React.Component {

  constructor(props) {
    super(props);
    const institution = JSON.parse(sessionStorage.getItem('institution'));
    this.state = {
      institution: institution.colorScheme,
      showModal: false,
      emailCode: '',
      emailConfirmed: '',
      documentNumber: '',
      documentType: '',
      name: '',
      contactPhone: '',
      contactEmail: '',
      contactName: '',
      lastName: '',
      birthDate: '',
      age: '',
      responsibleDocumentNumber: '',
      responsibleDocumentType: '',
      responsibleName: '',
      responsibleLastName: '',
      responsiblePhoneContact: '',
      responsiblePhoneContactConfirm: '',
      responsibleEmail: '',
      responsibleEmailConfirm: '',
      grade: {
        value: {
          levelId: '',
          programId: '',
          degreeId: '',
        }
      },
      validationCollector: [],
      showConfirmEmailModal: false,
      securityCode: '',
      loadingSecurity: false,
      shouldSendNotification: false,
      notificationData: null,
      shouldQueryLatestInscription: false,
      redirectUrl: ''
    }
    
    // Referencia para el scroll automático
    this.formContainerRef = React.createRef();
  }

  componentDidMount() {
    // Establecer título para identificar el componente en Analytics
    const institutionName = this.state?.institution?.institutionName || 'Institución';
    const screenTitle = `${institutionName} - Formulario de Inscripción`;
    if (typeof document !== 'undefined') {
      document.title = screenTitle;
    }
    // Enviar evento de vista de pantalla (identificador del componente)
    logEvent('screen_view', {
      screen_name: 'InscriptionForm',
      page_title: screenTitle,
      institution_name: institutionName
    });
    // Registrar el inicio del formulario incluyendo el contexto de pantalla
    logEvent(InscriptionEvents.FORM_START, {
      institutionId: this.getInstitutionIdFromToken(),
      scheduleId: this.state.institution.scheduledId,
      page_title: screenTitle,
      screen_name: 'InscriptionForm',
      institution_name: institutionName
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // Detectar cuando se establece un error y hacer scroll hacia arriba
    if (!prevState.errorMessage && this.state.errorMessage) {
      // Pequeño delay para asegurar que el DOM se haya actualizado
      setTimeout(() => {
        this.scrollToTop();
      }, 100);
    }
  }

  getDocumentTypeLabel = (docType) => {
    // Buscamos el tipo de documento en el array documentTypes
    const docTypeObj = documentTypes.find(doc => doc.value === docType);
    return docTypeObj ? docTypeObj.label : 'Tipo de documento no especificado';
  }

  setError(error) {
    this.setState({
      errorMessage: error
    });
  }

  // Método para hacer scroll hacia arriba cuando se muestra un error
  scrollToTop = () => {
    if (this.formContainerRef.current) {
      this.formContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      // Fallback: scroll hacia arriba de la página
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  // Método para obtener el token JWT desde sessionStorage
  getAuthToken = () => {
    return sessionStorage.getItem('authToken');
  }

  // Método para obtener el institutionId desde el token JWT
  getInstitutionIdFromToken = () => {
    const token = this.getAuthToken();
    if (!token) {
      console.error('No se encontró token JWT en sessionStorage');
      return null;
    }
    return getInstitutionIdFromToken(token);
  }

  handleChange = (e, label) => {
    if (label) {
      this.setState({
        [label]: e
      }, () => {
        // Si se está actualizando el tipo de documento o número de documento
        if ((label === 'documentType' || label === 'documentNumber') &&
          this.state.documentType &&
          this.state.documentType.value &&
          this.state.documentNumber) {
          // Resetear shouldQueryLatestInscription cuando se modifica el número de documento
          if (label === 'documentNumber') {
            this.setState({ shouldQueryLatestInscription: false });
          }
          // Forzar una validación después de que se carguen los datos
          setTimeout(() => {
            this.forceUpdate();
          }, 100);
        }
      });
    } else {
      const name = e.target.name;
      const value = e.target.value;
      this.setState({
        [name]: value
      }, () => {
        // Resetear shouldQueryLatestInscription cuando se modifica el número de documento
        if (name === 'documentNumber') {
          this.setState({ shouldQueryLatestInscription: false });
        }
      });
    }
  };

  handleDocumentNumberBlur = () => {
    // Activar la consulta cuando el usuario termine de escribir en el campo de número de documento
    if (this.state.documentType && this.state.documentType.value && this.state.documentNumber) {
      this.setState({ shouldQueryLatestInscription: true });
    }
  };

  closeModal = () => {
    this.setState({
      showModal: false
    })
  };

  openModal = () => {
    this.setState({
      showModal: true
    })
  };

  onChangeBirthDate = (date) => {
    let age = calculateAge(date)
    this.setState({
      birthDate: date,
      age: age
    });
  };

  collectValidations = (field, isValid) => {
    // Solo recolectar validaciones de campos que ya tienen valor
    if (this.state[field]) {
      this.setState(prevState => ({
        validationCollector: isValid
          ? prevState.validationCollector.filter(f => f !== field)
          : [...prevState.validationCollector, field]
      }));
    }
  }


  isAspirantComplete = () => {
    const {
      name,
      lastName,
      documentNumber,
      documentType,
      grade,
      birthDate
    } = this.state;

    // Validación del grado - verifica que se haya seleccionado una opción válida
    const isGradeValid = Boolean(
      grade &&
      grade.value &&
      grade.value.description
    );

    // Verificar que todos los campos del aspirante estén completos
    const isComplete = Boolean(
      name && name.trim() !== '' &&
      lastName && lastName.trim() !== '' &&
      documentNumber && documentNumber.trim() !== '' &&
      documentType && documentType.value &&
      isGradeValid &&
      birthDate instanceof Date && !isNaN(birthDate)
    );

    return isComplete;
  }

  isValid = () => {
    const {
      name,
      lastName,
      documentNumber,
      documentType,
      grade,
      birthDate,
      responsibleName,
      responsibleLastName,
      responsibleDocumentNumber,
      responsibleDocumentType,
      responsiblePhoneContact,
      responsiblePhoneContactConfirm,
      responsibleEmail,
      responsibleEmailConfirm
    } = this.state;

    // Función para validar email
    const isValidEmail = (email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return email && emailRegex.test(email);
    };

    // Función para validar celular colombiano
    const isValidColombianPhone = (phone) => {
      const phoneRegex = /^3[0-9]{9}$/;
      return phone && phoneRegex.test(phone);
    };

    // Validación del grado - verifica que se haya seleccionado una opción válida
    const isGradeValid = Boolean(
      grade &&
      grade.value &&
      grade.value.description
    );

    // Validaciones básicas del aspirante
    const aspirantValidations = {
      hasName: Boolean(name && name.trim() !== ''),
      hasLastName: Boolean(lastName && lastName.trim() !== ''),
      hasDocumentNumber: Boolean(documentNumber && documentNumber.trim() !== ''),
      hasDocumentType: Boolean(documentType && documentType.value),
      hasGrade: isGradeValid,
      hasBirthDate: Boolean(birthDate instanceof Date && !isNaN(birthDate))
    };
  
    const isAspirantValid = Object.values(aspirantValidations).every(validation => validation === true);

    // Validaciones del acudiente
    const responsibleValidations = {
      hasName: Boolean(responsibleName && responsibleName.trim() !== ''),
      hasLastName: Boolean(responsibleLastName && responsibleLastName.trim() !== ''),
      hasDocumentNumber: Boolean(responsibleDocumentNumber && responsibleDocumentNumber.trim() !== ''),
      hasDocumentType: Boolean(responsibleDocumentType && responsibleDocumentType.value),
      hasPhone: isValidColombianPhone(responsiblePhoneContact),
      hasPhoneConfirm: isValidColombianPhone(responsiblePhoneContactConfirm),
      hasEmail: isValidEmail(responsibleEmail),
      hasEmailConfirm: isValidEmail(responsibleEmailConfirm),
      phonesMatch: responsiblePhoneContact === responsiblePhoneContactConfirm,
      emailsMatch: responsibleEmail === responsibleEmailConfirm
    };

    // Verificar que todos los campos del acudiente estén completos y validados
    const isResponsibleComplete =
      responsibleValidations.hasName &&
      responsibleValidations.hasLastName &&
      responsibleValidations.hasDocumentNumber &&
      responsibleValidations.hasDocumentType &&
      responsibleValidations.hasPhone &&
      responsibleValidations.hasPhoneConfirm &&
      responsibleValidations.hasEmail &&
      responsibleValidations.hasEmailConfirm &&
      responsibleValidations.phonesMatch &&
      responsibleValidations.emailsMatch;

    // Verificar que los campos de contacto estén completos y validados
    const isContactComplete =
      responsibleValidations.hasPhone &&
      responsibleValidations.hasPhoneConfirm &&
      responsibleValidations.hasEmail &&
      responsibleValidations.hasEmailConfirm &&
      responsibleValidations.phonesMatch &&
      responsibleValidations.emailsMatch;

    // El formulario es válido solo si el aspirante está completo Y el acudiente está completo y validado
    const isFormValid = isAspirantValid && isResponsibleComplete && isContactComplete;

    return isFormValid;
  }

  handleDiligenciarClick = () => {
    const {
      name,
      lastName,
      documentNumber,
      documentType,
      grade,
      birthDate,
      responsibleName,
      responsibleLastName,
      responsibleDocumentNumber,
      responsibleDocumentType,
      responsiblePhoneContact,
      responsiblePhoneContactConfirm,
      responsibleEmail,
      responsibleEmailConfirm
    } = this.state;

    // Limpiar mensajes de error anteriores y notificaciones pendientes
    this.setState({ 
      errorMessage: null,
      shouldSendNotification: false,
      notificationData: null
    });

    // Validación del aspirante
    const aspirantErrors = [];
    if (!name || name.trim() === '') aspirantErrors.push('Nombre del aspirante');
    if (!lastName || lastName.trim() === '') aspirantErrors.push('Apellidos del aspirante');
    if (!documentNumber || documentNumber.trim() === '') aspirantErrors.push('Número de documento del aspirante');
    if (!documentType || !documentType.value) aspirantErrors.push('Tipo de documento del aspirante');
    if (!grade || !grade.value || !grade.value.description) aspirantErrors.push('Grado');
    if (!birthDate || !(birthDate instanceof Date) || isNaN(birthDate)) aspirantErrors.push('Fecha de nacimiento');

    // Si hay errores en el aspirante, mostrar mensaje y detener
    if (aspirantErrors.length > 0) {
      this.setState({
        errorMessage: {
          graphQLErrors: [{
            extensions: {
              code: 'VALIDATION_ERROR',
              message: `Por favor complete los siguientes campos del aspirante: ${aspirantErrors.join(', ')}`
            }
          }]
        }
      });
      return;
    }

    const responsibleErrors = [];
    const contactErrors = [];

    // Validación de datos básicos del acudiente
    if (!responsibleName || responsibleName.trim() === '') responsibleErrors.push('Nombre del acudiente');
    if (!responsibleLastName || responsibleLastName.trim() === '') responsibleErrors.push('Apellidos del acudiente');
    if (!responsibleDocumentNumber || responsibleDocumentNumber.trim() === '') responsibleErrors.push('Número de documento del acudiente');
    if (!responsibleDocumentType || !responsibleDocumentType.value) responsibleErrors.push('Tipo de documento del acudiente');

    // Validación de contacto
    const phoneRegex = /^3[0-9]{9}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Validación de celular
    if (!responsiblePhoneContact) {
      contactErrors.push('Celular del acudiente');
    } else if (!phoneRegex.test(responsiblePhoneContact)) {
      contactErrors.push('Celular del acudiente (formato válido: 3XXXXXXXXX)');
    }

    if (!responsiblePhoneContactConfirm) {
      contactErrors.push('Confirmación del celular del acudiente');
    } else if (!phoneRegex.test(responsiblePhoneContactConfirm)) {
      contactErrors.push('Confirmación del celular del acudiente (formato válido: 3XXXXXXXXX)');
    }

    if (responsiblePhoneContact && responsiblePhoneContactConfirm &&
      responsiblePhoneContact !== responsiblePhoneContactConfirm) {
      contactErrors.push('Los números de celular no coinciden');
    }

    // Validación de email
    if (!responsibleEmail) {
      contactErrors.push('Correo electrónico del acudiente');
    } else if (!emailRegex.test(responsibleEmail)) {
      contactErrors.push('Correo electrónico del acudiente (formato inválido)');
    }

    if (!responsibleEmailConfirm) {
      contactErrors.push('Confirmación del correo electrónico del acudiente');
    } else if (!emailRegex.test(responsibleEmailConfirm)) {
      contactErrors.push('Confirmación del correo electrónico del acudiente (formato inválido)');
    }

    if (responsibleEmail && responsibleEmailConfirm &&
      responsibleEmail !== responsibleEmailConfirm) {
      contactErrors.push('Los correos electrónicos no coinciden');
    }

    // Si hay errores en los datos básicos del acudiente, mostrar mensaje
    if (responsibleErrors.length > 0) {
      this.setState({
        errorMessage: {
          graphQLErrors: [{
            extensions: {
              code: 'VALIDATION_ERROR',
              message: `Por favor complete los siguientes campos del acudiente: ${responsibleErrors.join(', ')}`
            }
          }]
        }
      });
      return;
    }

    // Si hay errores en los datos de contacto, mostrar mensaje
    if (contactErrors.length > 0) {
      this.setState({
        errorMessage: {
          graphQLErrors: [{
            extensions: {
              code: 'VALIDATION_ERROR',
              message: `Por favor complete los siguientes datos de contacto: ${contactErrors.join(', ')}`
            }
          }]
        }
      });
      return;
    }

    // Si todo está correcto, proceder con la mutación
    if (this.confirmInscriptionRef) {
      // Verificar que todas las variables requeridas estén disponibles
      const requiredFields = {
        affiliateName: this.state.responsibleName,
        affiliateLastName: this.state.responsibleLastName,
        affiliateDocType: this.state.responsibleDocumentType?.value,
        affiliateDocument: this.state.responsibleDocumentNumber,
        beneficiaryDocType: this.state.documentType?.value,
        beneficiaryDocument: this.state.documentNumber,
        beneficiaryName: this.state.name,
        beneficiaryLastName: this.state.lastName,
        levelId: this.state.grade?.value?.levelId,
        programId: this.state.grade?.value?.programId,
        degreeId: this.state.grade?.value?.degreeId,
        birthDate: this.state.birthDate,
        institutionId: this.getInstitutionIdFromToken(),
        scheduleId: this.state.institution?.scheduledId,
        email: this.state.responsibleEmail,
        phoneContact: this.state.responsiblePhoneContact
      };

      // Verificar que no haya valores undefined o null
      const missingFields = Object.entries(requiredFields)
        .filter(([key, value]) => value === undefined || value === null || value === '')
        .map(([key]) => key);

      if (missingFields.length > 0) {
        console.error('Campos faltantes para el mutation:', missingFields);
        this.setState({
          errorMessage: {
            graphQLErrors: [{
              extensions: {
                code: 'VALIDATION_ERROR',
                message: `Campos faltantes: ${missingFields.join(', ')}`
              }
            }]
          }
        });
        return;
      }

      // Registrar el intento de envío
      logEvent(InscriptionEvents.FORM_SUBMIT_ATTEMPT, {
        institutionId: this.getInstitutionIdFromToken(),
        scheduleId: this.state.institution.scheduledId
      });
      
      const mutationPromise = this.confirmInscriptionRef();
      
      // Verificar que la función devuelva una promesa válida
      if (mutationPromise && typeof mutationPromise.then === 'function') {
        mutationPromise
          .then((result) => {
            // Verificar que result y result.data existan antes de hacer destructuring
            if (!result) {
              console.log('No hay resultado de la mutación');
              return;
            }

            const { data } = result;
            console.log('Respuesta de la mutación:', data);
            
            if (data && data.saveInscriptionCiber) {
              if (data.saveInscriptionCiber.statusCode === "CIB-GEN-0200") {
                // Mostrar el modal de confirmación de email
                this.setState({ 
                  showConfirmEmailModal: true,
                  redirectUrl: data.saveInscriptionCiber.redirectUrl,
                  shouldSendNotification: false, // Limpiar el estado para evitar conflictos
                  notificationData: null
                }, () => {
                  // Ejecutar el query de notificación de email
                  const url = data.saveInscriptionCiber.redirectUrl;
                  let eventId = 0; // valor por defecto seguro
                  try {
                    if (url) {
                      const params = new URL(url).searchParams;
                      eventId = parseInt(params.get('p_id_dato')) || 0;
                    }
                  } catch (e) {
                    this.setError({
                      graphQLErrors: [{
                        extensions: {
                          code: 'EXTRACTION_ERROR',
                          message: 'No se pudo extraer el código de evento de la URL de redirección.'
                        }
                      }]
                    });
                    return; // No ejecutar el query si hay error
                  }
                  // Ejecutar notificación directamente sin usar el estado
                  this.executeSendNotificationEmail(eventId, 29, "", "", "Inscription", "", (error, result) => {
                    if (error) {
                      console.error('Error al enviar notificación de email:', error);
                    } else {
                      console.log('Notificación de email enviada exitosamente');
                    }
                  });
                });

                // Registrar el éxito de la inscripción
                logEvent(InscriptionEvents.FORM_COMPLETE, {
                  institutionId: this.getInstitutionIdFromToken(),
                  scheduleId: this.state.institution.scheduledId,
                  status: 'success'
                });
              } else {
                // Ejecutar notificación directamente para errores que no son "CIB-GEN-0200"
                const eventId = parseInt(data.saveInscriptionCiber.message) || 0;
                this.executeSendNotificationEmail(eventId, 132, "", "", "Inscription", "", (error, result) => {
                  if (error) {
                    console.error('Error al enviar notificación:', error);
                    logEvent(InscriptionEvents.NOTIFICATION_ERROR, {
                      institutionId: this.getInstitutionIdFromToken(),
                      scheduleId: this.state.institution.scheduledId,
                      error: error.message
                    });
                  } else {
                    console.log('Notificación enviada exitosamente');
                    logEvent(InscriptionEvents.NOTIFICATION_SENT, {
                      institutionId: this.getInstitutionIdFromToken(),
                      scheduleId: this.state.institution.scheduledId,
                      statusCode: data.saveInscriptionCiber.statusCode
                    });
                  }
                });

                const errorMessage = {
                  graphQLErrors: [{
                    extensions: {
                      code: data.saveInscriptionCiber.statusCode || 'ANY'
                    }
                  }]
                };
                this.setState({ errorMessage });

                // Registrar el error
                logEvent(InscriptionEvents.FORM_ERROR, {
                  institutionId: this.getInstitutionIdFromToken(),
                  scheduleId: this.state.institution.scheduledId,
                  error: data.saveInscriptionCiber.message
                });
              }
            } else {
              console.error('Respuesta de mutación sin datos válidos:', data);
            }
          });
      } else {
        console.error('confirmInscriptionRef no devolvió una promesa válida:', mutationPromise);
        // Manejar el error cuando la función no devuelve una promesa
        this.setState({
          errorMessage: {
            graphQLErrors: [{
              extensions: {
                code: 'MUTATION_ERROR',
                message: 'Error al ejecutar la mutación'
              }
            }]
          }
        });
      }
    } else {
      console.error('confirmInscriptionRef no está disponible');
    }
  }

  handleSendSecurityCode = () => {
    this.setState({ loadingSecurity: true });
    
    // Consumir el servicio getLatestInscription para obtener el emailCode
    if (this.apolloClient) {
      this.apolloClient.query({
        query: LatestInscriptionQuery,
        variables: {
          beneficiaryDocType: this.state.documentType.value,
          beneficiaryDocument: this.state.documentNumber,
          institutionId: this.getInstitutionIdFromToken(),
          scheduleId: this.state.institution.scheduledId
        },
        fetchPolicy: "no-cache"
      }).then(result => {
        const inscription = result.data.getLatestInscription;
        
        if (inscription && inscription.emailCode) {
          // Comparar el código ingresado con el emailCode
          if (this.state.securityCode === inscription.emailCode) {
            // Códigos coinciden - validación exitosa
            console.log('Validación de código exitosa');
            
            // Actualizar emailConfirmed a 'S' antes de redirigir
            this.updateEmailConfirmed();
          } else {
            // Códigos no coinciden
            console.log('Código incorrecto');
            alert('Código incorrecto. Por favor, verifique e inténtelo de nuevo.');
            this.setState({ loadingSecurity: false });
          }
        } else {
          // No se encontró emailCode en la respuesta
          console.error('No se encontró emailCode en la respuesta');
          alert('Error: No se pudo validar el código. Por favor, inténtelo de nuevo.');
          this.setState({ loadingSecurity: false });
        }
      }).catch(error => {
        console.error('Error al obtener datos de inscripción:', error);
        alert('Error al validar el código. Por favor, inténtelo de nuevo.');
        this.setState({ loadingSecurity: false });
      });
    } else {
      console.error('ApolloClient no disponible');
      alert('Error: No se pudo conectar al servicio. Por favor, inténtelo de nuevo.');
      this.setState({ loadingSecurity: false });
    }
  }

  updateEmailConfirmed = () => {
    // Ejecutar la mutación para actualizar emailConfirmed a 'S'
    if (this.apolloClient) {
      this.apolloClient.mutate({
        mutation: InscriptionMutation,
        variables: {
          dataInscription: {
            affiliateName: this.state.responsibleName,
            affiliateLastName: this.state.responsibleLastName,
            affiliateDocType: this.state.responsibleDocumentType.value,
            affiliateDocument: this.state.responsibleDocumentNumber,
            beneficiaryDocType: this.state.documentType.value,
            beneficiaryDocument: this.state.documentNumber,
            beneficiaryName: this.state.name,
            beneficiaryLastName: this.state.lastName,
            levelId: this.state.grade.value.levelId,
            programId: this.state.grade.value.programId,
            degreeId: this.state.grade.value.degreeId,
            birthDate: this.state.birthDate,
            deviceId: getDeviceId(),
            institutionId: this.getInstitutionIdFromToken(),
            scheduleId: this.state.institution.scheduledId,
            email: this.state.responsibleEmail,
            phoneContact: this.state.responsiblePhoneContact,
            emailConfirmed: 'S', // Marcar email como confirmado
            updatedBy: 1
          }
        }
      }).then(result => {
        console.log('Email confirmado exitosamente:', result);
        
        // Mostrar mensaje de éxito
        alert('¡Validación exitosa! Redirigiendo...');
        
        // Esperar 2 segundos y redirigir
        setTimeout(() => {
          if (this.state.redirectUrl) {
            window.location.href = this.state.redirectUrl;
          } else {
            console.error('No se encontró URL de redirección');
            alert('Error: No se encontró URL de redirección');
          }
        }, 2000);
        
        // Registrar el éxito de la confirmación
        logEvent(InscriptionEvents.EMAIL_CONFIRMATION_SUCCESS, {
          institutionId: this.getInstitutionIdFromToken(),
          scheduleId: this.state.institution.scheduledId
        });
        
        // Cerrar la modal
        this.setState({ 
          showConfirmEmailModal: false, 
          loadingSecurity: false 
        });
      }).catch(error => {
        console.error('Error al actualizar emailConfirmed:', error);
        alert('Error al confirmar el email. Por favor, inténtelo de nuevo.');
        this.setState({ loadingSecurity: false });
      });
    } else {
      console.error('ApolloClient no disponible para actualizar emailConfirmed');
      alert('Error: No se pudo conectar al servicio. Por favor, inténtelo de nuevo.');
      this.setState({ loadingSecurity: false });
    }
  }

  handleResendCode = (e) => {
    e.preventDefault();
    // Registrar el reenvío del código
    logEvent(InscriptionEvents.EMAIL_CONFIRMATION_RESENT, {
      institutionId: this.getInstitutionIdFromToken(),
      scheduleId: this.state.institution.scheduledId,
      email: this.state.responsibleEmail
    });
    
    // Llamar a la función de reenvío
    this.resendNotificationCode();
  }

  resendNotificationCode = () => {
    // Obtener el eventId de la URL de redirección almacenada en el estado
    let eventId = 0; // valor por defecto seguro
    try {
      // Buscar el eventId en el estado o en la URL de redirección
      if (this.state.redirectUrl) {
        const params = new URL(this.state.redirectUrl).searchParams;
        eventId = parseInt(params.get('p_id_dato')) || 0;
      }
    } catch (e) {
      console.error('Error al extraer eventId para reenvío:', e);
      // Si no se puede extraer el eventId, usar 0 como valor por defecto
      eventId = 0;
    }
    
    // Ejecutar el servicio de sendnotification para reenviar el código
    this.executeSendNotificationEmail(eventId, 29, "", "", "Inscription", "", (error, result) => {
      if (error) {
        console.error('Error al reenviar notificación:', error);
      } else {
        alert('El código de confirmación ha sido reenviado exitosamente');
      }
    });
  }



  // Modifico el método para aceptar un callback opcional
  executeSendNotificationEmail = (eventId, typeId, data, subject, data2, data3, callback) => {
    if (this.apolloClient) {
      this.apolloClient.query({
        query: SendNotificationQuery,
        variables: {
          eventId,
          institutionId: this.getInstitutionIdFromToken(),
          scheduleId: this.state.institution.scheduledId,
          typeId,
          data: data || "",
          subject: subject || "",
          data2: data2 || "",
          data3: data3 || ""
        },
        fetchPolicy: "no-cache"
      }).then(result => {
        if (callback) callback(null, result);
      }).catch(error => {
        if (callback) callback(error);
      });
    } else {
      if (callback) callback(new Error('ApolloClient no disponible'));
    }
  };

  render() {
    if (!this.state.institution) {
      return <div>Cargando...</div>;
    }

    const isAspirantComplete = this.isAspirantComplete();
    const isFormValid = this.isValid();

    return (
      <ApolloConsumer>
        {client => {
          this.apolloClient = client;
          return (
            <div className="container" ref={this.formContainerRef}>
              <div className="row justify-content-center">
                <div className="col-md-10">
                  <div className="card" style={{
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    marginBottom: '2rem'
                  }}>
                    <div className="card-body" style={{ padding: '2rem' }}>
                      {this.state.errorMessage ?
                        <CiberAlerts error={this.state.errorMessage} /> :
                        null
                      }
                      <Query
                        key={`${this.state.documentType?.value}-${this.state.documentNumber}-${this.state.shouldQueryLatestInscription}`}
                        query={LatestInscriptionQuery}
                        variables={{
                          beneficiaryDocType: this.state.documentType && this.state.documentType.value ? this.state.documentType.value : 0,
                          beneficiaryDocument: this.state.documentNumber || '',
                          institutionId: this.getInstitutionIdFromToken(),
                          scheduleId: this.state.institution.scheduledId
                        }}
                        skip={!(this.state.documentType && this.state.documentType.value && this.state.documentNumber && this.state.shouldQueryLatestInscription)}
                        onCompleted={(data) => {
                          if (data.getLatestInscription && Object.keys(data.getLatestInscription).length > 0) {
                            const inscription = data.getLatestInscription;
                            let birthDate = null;
                            if (inscription.birthDate) {
                              const [year, month, day] = inscription.birthDate.split('-');
                              birthDate = new Date(year, month - 1, day);
                            }
                            this.setState({
                              name: inscription.beneficiaryName || '',
                              lastName: inscription.beneficiaryLastName || '',
                              birthDate: birthDate,
                              age: birthDate ? calculateAge(birthDate) : '',
                              responsibleName: inscription.affiliateName || '',
                              responsibleLastName: inscription.affiliateLastName || '',
                              responsibleDocumentNumber: inscription.affiliateDocument || '',
                              responsibleDocumentType: {
                                value: inscription.affiliateDocType || '',
                                label: this.getDocumentTypeLabel(inscription.affiliateDocType)
                              },
                              contactName: inscription.contact || '',
                              responsiblePhoneContact: (inscription.phoneContact || '').toString().trim(),
                              responsiblePhoneContactConfirm: '',
                              responsibleEmail: (inscription.email || '').toString().trim(),
                              responsibleEmailConfirm: '',
                              redirectUrl: inscription.redirectUrl
                            }, () => {
                              // Forzar una validación después de cargar los datos
                              this.forceUpdate();
                            });
                          } else {
                            // Limpiar el formulario cuando no hay información
                            this.setState({
                              name: '',
                              lastName: '',
                              birthDate: '',
                              age: '',
                              responsibleName: '',
                              responsibleLastName: '',
                              responsibleDocumentNumber: '',
                              responsibleDocumentType: '',
                              contactName: '',
                              responsiblePhoneContact: '',
                              responsiblePhoneContactConfirm: '',
                              responsibleEmail: '',
                              responsibleEmailConfirm: '',
                              redirectUrl: ''
                            }, () => {
                              // Forzar una validación después de limpiar los datos
                              this.forceUpdate();
                            });
                          }
                        }}
                        onError={(error) => {
                          // También limpiar el formulario en caso de error
                          this.setState({
                            name: '',
                            lastName: '',
                            birthDate: '',
                            age: '',
                            responsibleName: '',
                            responsibleLastName: '',
                            responsibleDocumentNumber: '',
                            responsibleDocumentType: '',
                            contactName: '',
                            responsiblePhoneContact: '',
                            responsiblePhoneContactConfirm: '',
                            responsibleEmail: '',
                            responsibleEmailConfirm: '',
                            redirectUrl: ''
                          }, () => {
                            this.forceUpdate();
                          });
                        }}
                      >
                        {({ loading, error }) => {
                          if (loading) return (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              padding: '1rem',
                              color: 'var(--text-color)',
                              whiteSpace: 'nowrap'
                            }}>
                              <Spinner animation="border" role="status" style={{ marginRight: '0.5rem' }}>
                                <span style={{ display: 'none' }}>Cargando...</span>
                              </Spinner>
                              <span>Cargando...</span>
                            </div>
                          );
                          if (error) return null;
                          return null;
                        }}
                      </Query>
                      <Mutation mutation={InscriptionMutation}
                        variables={{
                          dataInscription: {
                            affiliateName: this.state.responsibleName,
                            affiliateLastName: this.state.responsibleLastName,
                            affiliateDocType: this.state.responsibleDocumentType.value,
                            affiliateDocument: this.state.responsibleDocumentNumber,
                            beneficiaryDocType: this.state.documentType.value,
                            beneficiaryDocument: this.state.documentNumber,
                            beneficiaryName: this.state.name,
                            beneficiaryLastName: this.state.lastName,
                            levelId: this.state.grade.value.levelId,
                            programId: this.state.grade.value.programId,
                            degreeId: this.state.grade.value.degreeId,
                            birthDate: this.state.birthDate,
                            deviceId: getDeviceId(),
                            institutionId: this.getInstitutionIdFromToken(),
                            scheduleId: this.state.institution.scheduledId,
                            email: this.state.responsibleEmail,
                            phoneContact: this.state.responsiblePhoneContact,
                            updatedBy: 1,
                            createdBy: 1
                          }
                        }}
                        onError={(error) => {
                          // Extraer el código de error de GraphQL
                          let errorCode = 'ANY';
                          let graphQLErrorMessage = '6522'; // Valor por defecto
                          
                          if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                            const graphQLError = error.graphQLErrors[0];
                            console.log('GraphQL Error en onError:', graphQLError);
                            if (graphQLError.extensions && graphQLError.extensions.code) {
                              errorCode = graphQLError.extensions.code;
                            }
                            // Usar el message del error de GraphQL como eventId
                            if (graphQLError.message) {
                              graphQLErrorMessage = graphQLError.message;
                            }
                          }

                          // Ejecutar notificación directamente cuando hay error de GraphQL
                          const eventId = parseInt(graphQLErrorMessage) || 0;
                          this.executeSendNotificationEmail(eventId, 132, "", "", "Inscription", "", (error, result) => {
                            if (error) {
                              console.error('Error al enviar notificación:', error);
                              logEvent(InscriptionEvents.NOTIFICATION_ERROR, {
                                institutionId: this.getInstitutionIdFromToken(),
                                scheduleId: this.state.institution.scheduledId,
                                error: error.message
                              });
                            } else {
                              console.log('Notificación enviada exitosamente');
                              logEvent(InscriptionEvents.NOTIFICATION_SENT, {
                                institutionId: this.getInstitutionIdFromToken(),
                                scheduleId: this.state.institution.scheduledId,
                                statusCode: errorCode
                              });
                            }
                          });

                          // Estructurar el error de manera consistente
                          const errorMessage = {
                            graphQLErrors: [{
                              extensions: {
                                code: errorCode
                              }
                            }]
                          };
                          this.setState({ errorMessage });

                          // Registrar el error
                          logEvent(InscriptionEvents.FORM_ERROR, {
                            institutionId: this.getInstitutionIdFromToken(),
                            scheduleId: this.state.institution.scheduledId,
                            error: error.message
                          });
                        }}>
                        {(confirmInscription, result) => {
                          // Guardamos la referencia para usarla después del modal
                          if (confirmInscription && typeof confirmInscription === 'function') {
                            this.confirmInscriptionRef = confirmInscription;
                          } else {
                            console.error('confirmInscription no está disponible');
                            this.confirmInscriptionRef = null;
                          }

                          return (
                            <>
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="card-body" style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '16px',
                                    marginBottom: '1.5rem',
                                    padding: '2rem',
                                    height: '100%',
                                    position: 'relative',
                                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                                    boxShadow: `0 8px 16px ${this.props.background}1A`
                                  }}>
                                    <div style={{
                                      position: 'absolute',
                                      top: '1.5rem',
                                      left: '-1rem',
                                      width: '3rem',
                                      height: '3rem',
                                      backgroundColor: this.props.background,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '1.2rem',
                                      boxShadow: `0 4px 8px ${this.props.background}33`
                                    }}>
                                      1
                                    </div>
                                    <h5 style={{
                                      fontWeight: "bold",
                                      color: 'var(--text-color)',
                                      marginBottom: '2rem',
                                      paddingLeft: '2rem',
                                      fontSize: '1.40rem',
                                      borderBottom: `3px solid ${this.props.background}`,
                                      paddingBottom: '0.75rem',
                                      textAlign: 'left'
                                    }}>Información del aspirante</h5>
                                    <DocumentTypes handleChange={this.handleChange} documentType={this.state.documentType}
                                      label="Tipo de documento del aspirante" name="documentType"
                                      style={{ marginBottom: '1.5rem' }}
                                    />
                                    <InscriptionInput value={this.state.documentNumber} label="N° de documento del aspirante"
                                      name="documentNumber" handleChange={this.handleChange} validation="alphanumeric" collectValidations={this.collectValidations} handleBlur={this.handleDocumentNumberBlur} />
                                    <InscriptionInput value={this.state.name} label="Nombres del aspirante" name="name"
                                      handleChange={this.handleChange} validation="text" collectValidations={this.collectValidations} />
                                    <InscriptionInput value={this.state.lastName} label="Apellidos del aspirante" name="lastName"
                                      handleChange={this.handleChange} validation="text" collectValidations={this.collectValidations} />
                                    <label className="form-label-custom" style={{ 
                                      color: 'var(--text-color)',
                                      fontSize: '0.9rem',
                                      fontWeight: '500',
                                      marginBottom: '0.5rem',
                                      display: 'block',
                                      textAlign: 'left'
                                    }}>Fecha de Nacimiento</label>
                                    <DatePicker
                                      selected={this.state.birthDate}
                                      onChange={this.onChangeBirthDate}
                                      maxDate={new Date()}
                                      dateFormat="dd/MM/yyyy"
                                      className="form-control"
                                      wrapperClassName="w-100"
                                      showYearDropdown
                                      showMonthDropdown
                                      dropdownMode="select"
                                      yearDropdownItemNumber={100}
                                      scrollableYearDropdown
                                      locale="es"
                                      customInput={
                                        <input
                                          style={{
                                            width: '100%',
                                            padding: '0.75rem 1rem',
                                            fontSize: '1rem',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '8px',
                                            backgroundColor: 'var(--input-bg)',
                                            color: 'var(--text-color)',
                                            transition: 'all 0.3s ease'
                                          }}
                                        />
                                      }
                                    />
                                    <br />
                                    <br />
                                    <InscriptionInput value={this.state.age} label="Edad" readOnly={true} />
                                    <Grades grade={this.state.grade} service={DegreesQuery}
                                      variables={{ institutionId: this.getInstitutionIdFromToken(), scheduleId: this.state.institution.scheduledId }}
                                      index="degreesInscription" handleChange={this.handleChange}
                                    />
                                  </div>
                                </div>

                                <div className="col-md-6">
                                  <div className="card-body" style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '16px',
                                    marginBottom: '1.5rem',
                                    padding: '2rem',
                                    height: '100%',
                                    position: 'relative',
                                    background: isAspirantComplete
                                      ? 'linear-gradient(145deg, #ffffff 0%, #f0f7ff 100%)'
                                      : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                                    boxShadow: isAspirantComplete
                                      ? `0 8px 16px ${this.props.background}1A`
                                      : `0 8px 16px ${LightenDarkenColor(this.props.background, 60)}1A`,
                                    transition: 'all 0.3s ease',
                                    opacity: isAspirantComplete ? '1' : '0.7',
                                    pointerEvents: isAspirantComplete ? 'auto' : 'none'
                                  }}>
                                    <div style={{
                                      position: 'absolute',
                                      top: '1.5rem',
                                      left: '-1rem',
                                      width: '3rem',
                                      height: '3rem',
                                      backgroundColor: isAspirantComplete ? this.props.background : LightenDarkenColor(this.props.background, 60),
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '1.2rem',
                                      boxShadow: isAspirantComplete
                                        ? `0 4px 8px ${this.props.background}33`
                                        : `0 4px 8px ${LightenDarkenColor(this.props.background, 60)}33`
                                    }}>
                                      2
                                    </div>
                                    <h5 style={{
                                      fontWeight: "bold",
                                      color: 'var(--text-color)',
                                      marginBottom: '2rem',
                                      paddingLeft: '2rem',
                                      fontSize: '1.40rem',
                                      borderBottom: `3px solid ${isAspirantComplete ? this.props.background : LightenDarkenColor(this.props.background, 60)}`,
                                      paddingBottom: '0.75rem',
                                      textAlign: 'left'
                                    }}>Información del acudiente principal</h5>
                                    <DocumentTypes
                                      handleChange={this.handleChange}
                                      documentType={this.state.responsibleDocumentType}
                                      label="Tipo de Documento del acudiente principal"
                                      name="responsibleDocumentType"
                                      disabled={!isAspirantComplete}
                                    />
                                    <InscriptionInput
                                      value={this.state.responsibleDocumentNumber}
                                      label="N° de documento del acudiente principal"
                                      name="responsibleDocumentNumber"
                                      handleChange={this.handleChange}
                                      validation="alphanumeric"
                                      collectValidations={this.collectValidations}
                                      disabled={!isAspirantComplete}
                                    />
                                    <InscriptionInput
                                      value={this.state.responsibleName}
                                      label="Nombres del acudiente principal"
                                      name="responsibleName"
                                      handleChange={this.handleChange}
                                      validation="text"
                                      collectValidations={this.collectValidations}
                                      disabled={!isAspirantComplete}
                                    />
                                    <InscriptionInput
                                      value={this.state.responsibleLastName}
                                      handleChange={this.handleChange}
                                      label="Apellidos del acudiente principal"
                                      name="responsibleLastName"
                                      validation="text"
                                      collectValidations={this.collectValidations}
                                      disabled={!isAspirantComplete}
                                    />
                                    <InscriptionInput
                                      value={this.state.responsiblePhoneContact}
                                      handleChange={this.handleChange}
                                      label="Celular del acudiente principal"
                                      name="responsiblePhoneContact"
                                      validation="colombianPhone"
                                      collectValidations={this.collectValidations}
                                      autoComplete="off"
                                      disabled={!isAspirantComplete}
                                    />
                                    <InscriptionInput
                                      value={this.state.responsiblePhoneContactConfirm}
                                      handleChange={this.handleChange}
                                      label="Confirmar celular del acudiente principal"
                                      name="responsiblePhoneContactConfirm"
                                      validation="colombianPhone"
                                      collectValidations={this.collectValidations}
                                      autoComplete="off"
                                      disabled={!isAspirantComplete}
                                    />
                                    <InscriptionInput
                                      value={this.state.responsibleEmail}
                                      handleChange={this.handleChange}
                                      label="Correo electrónico del acudiente principal"
                                      name="responsibleEmail"
                                      validation="email"
                                      collectValidations={this.collectValidations}
                                      autoComplete="off"
                                      disabled={!isAspirantComplete}
                                    />
                                    <InscriptionInput
                                      value={this.state.responsibleEmailConfirm}
                                      handleChange={this.handleChange}
                                      label="Confirmar correo electrónico del acudiente principal"
                                      name="responsibleEmailConfirm"
                                      validation="email"
                                      collectValidations={this.collectValidations}
                                      autoComplete="off"
                                      disabled={!isAspirantComplete}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="row justify-content-center mt-4">
                                <div className="col-md-8">
                                  <button
                                    className="btn btn-lg btn-primary btn-block"
                                    type="button"
                                    onClick={this.handleDiligenciarClick}
                                    disabled={!isFormValid || result.loading}
                                    style={{
                                      background: isFormValid && !result.loading
                                        ? `linear-gradient(90deg, ${this.props.background} 0%, ${LightenDarkenColor(this.props.background, -40)} 100%)`
                                        : `linear-gradient(90deg, ${LightenDarkenColor(this.props.background, 60)} 0%, ${LightenDarkenColor(this.props.background, 30)} 100%)`,
                                      border: 'none',
                                      borderRadius: '2rem',
                                      padding: '0.85rem 2.2rem',
                                      fontSize: '1.15rem',
                                      fontWeight: '700',
                                      color: '#fff',
                                      letterSpacing: '0.03em',
                                      boxShadow: isFormValid && !result.loading
                                        ? `0 4px 16px ${this.props.background}33`
                                        : `0 2px 8px ${LightenDarkenColor(this.props.background, 60)}33`,
                                      opacity: isFormValid && !result.loading ? '1' : '0.7',
                                      cursor: isFormValid && !result.loading ? 'pointer' : 'not-allowed',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '0.7rem',
                                      transition: 'all 0.25s cubic-bezier(.4,2,.6,1)',
                                      margin: '0 auto',
                                      width: '100%',
                                      boxSizing: 'border-box',
                                      outline: 'none',
                                      position: 'relative',
                                      zIndex: 1
                                    }}
                                    onMouseOver={e => {
                                      if (isFormValid && !result.loading) {
                                        e.currentTarget.style.background = `linear-gradient(90deg, ${LightenDarkenColor(this.props.background, -40)} 0%, ${this.props.background} 100%)`;
                                        e.currentTarget.style.boxShadow = `0 8px 24px ${this.props.background}55`;
                                      }
                                    }}
                                    onMouseOut={e => {
                                      if (isFormValid && !result.loading) {
                                        e.currentTarget.style.background = `linear-gradient(90deg, ${this.props.background} 0%, ${LightenDarkenColor(this.props.background, -40)} 100%)`;
                                        e.currentTarget.style.boxShadow = `0 4px 16px ${this.props.background}33`;
                                      }
                                    }}
                                  >
                                    Diligenciar Inscripción
                                    {result.loading ? (
                                      <>
                                        <Spinner animation="border" role="status" size="sm" style={{ marginLeft: '0.5rem' }}>
                                          <span style={{ display: 'none' }}>Cargando...</span>
                                        </Spinner>
                                        <span style={{ fontWeight: 400, fontSize: '1rem' }}>Cargando...</span>
                                      </>
                                    ) : null}
                                  </button>
                                </div>
                              </div>

                              <ModalConfirmEmail
                                show={this.state.showConfirmEmailModal}
                                onHide={() => this.setState({ showConfirmEmailModal: false })}
                                onSubmit={this.handleSendSecurityCode}
                                onResend={this.handleResendCode}
                                emailMasked={this.state.responsibleEmail}
                                code={this.state.securityCode}
                                setCode={(code) => this.setState({ securityCode: code })}
                                loading={this.state.loadingSecurity}
                              />

                              {/* Las notificaciones ahora se ejecutan directamente en los métodos correspondientes */}
                            </>
                          );
                        }}
                      </Mutation>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </ApolloConsumer>
    )
  }
}
